import { NavLink, Outlet, useNavigate } from "react-router-dom";
import ErrorBoundary from "@/components/ErrorBoundary";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard, HardHat, Users, CalendarCheck, FileText,
  Receipt, Sparkles, LogOut, Menu, Sun, Moon, Truck, Settings, Calendar, Contact, Bot,
  Package, UserCircle2, ChevronDown, ChevronRight, Shield, Folder
} from "lucide-react";
import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { api } from "@/lib/api";
import InstallPWA from "@/components/InstallPWA";
import VoiceAssistant from "@/components/VoiceAssistant";
import { DuplicateResolver } from "@/components/DuplicateResolver";
import { MagazzinoReview } from "@/components/MagazzinoReview";
import { PagamentoMetodoDialog } from "@/components/PagamentoMetodoDialog";

const ADMIN_NAV = [
  { type: "link", to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { type: "group", key: "cantieri", label: "Gestione Cantieri", icon: HardHat, testid: "nav-group-cantieri", children: [
    { to: "/cantieri", label: "Cantieri", icon: HardHat, testid: "nav-cantieri" },
    { to: "/clienti", label: "Clienti", icon: Contact, testid: "nav-clienti" },
    { to: "/rendiconti", label: "Rendiconti", icon: FileText, testid: "nav-rendiconti", badgeKey: "pending_rendiconti" },
    { to: "/appuntamenti", label: "Appuntamenti", icon: Calendar, testid: "nav-appuntamenti" },
  ]},
  { type: "group", key: "personale", label: "Gestione Personale", icon: Users, testid: "nav-group-personale", children: [
    { to: "/operai", label: "Operai & Team", icon: Users, testid: "nav-operai" },
    { to: "/presenze", label: "Presenze", icon: CalendarCheck, testid: "nav-presenze" },
    { to: "/archivio-hr", label: "Archivio HR", icon: Folder, testid: "nav-hr" },
  ]},
  { type: "group", key: "admin", label: "Amministrazione", icon: Receipt, testid: "nav-group-admin", children: [
    { to: "/fatture", label: "Fatture & IVA", icon: Receipt, testid: "nav-fatture" },
    { to: "/fornitori", label: "Fornitori & DDT", icon: Truck, testid: "nav-fornitori" },
    { to: "/commercialista", label: "Commercialista", icon: Receipt, testid: "nav-commercialista" },
  ]},
  { type: "link", to: "/magazzino", label: "Magazzino", icon: Package, testid: "nav-magazzino" },
  { type: "link", to: "/ai-assistente", label: "Chiedi a Cantiere360", icon: Bot, testid: "nav-ai-chat", badgeKey: "anomalie" },
  { type: "link", to: "/ai-report", label: "AI Report", icon: Sparkles, testid: "nav-ai" },
  { type: "link", to: "/impostazioni", label: "Impostazioni", icon: Settings, testid: "nav-impostazioni" },
];

const SIMPLE_NAV = {
  capocantiere: [
    { to: "/", label: "Timbra", icon: CalendarCheck, testid: "nav-timbra" },
    { to: "/cantieri", label: "Cantieri (lavori)", icon: HardHat, testid: "nav-cantieri" },
    { to: "/rendiconti", label: "Rendiconti", icon: FileText, testid: "nav-rendiconti" },
    { to: "/presenze", label: "Le mie ore", icon: FileText, testid: "nav-mie-ore" },
    { to: "/magazzino", label: "Magazzino", icon: Package, testid: "nav-magazzino" },
  ],
  operaio: [
    { to: "/", label: "Timbra", icon: CalendarCheck, testid: "nav-timbra" },
    { to: "/rendiconti", label: "Rendiconti", icon: FileText, testid: "nav-rendiconti" },
    { to: "/presenze", label: "Le mie ore & paga", icon: FileText, testid: "nav-mie-ore" },
  ],
};

export default function Layout() {
  const { user, logout } = useAuth();
  const { theme, toggle: toggleTheme } = useTheme();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [groups, setGroups] = useState({ cantieri: true, personale: true, admin: true });
  const [badges, setBadges] = useState({ pending_rendiconti: 0, anomalie: 0 });

  useEffect(() => {
    if (user?.role !== "admin") return;
    let stopped = false;
    const tick = () => {
      api.get("/rendiconti/pending-count").then(({ data }) => { if (!stopped) setBadges((b) => ({ ...b, pending_rendiconti: data.count || 0 })); }).catch(() => {});
      api.get("/ai/anomalie").then(({ data }) => { if (!stopped) setBadges((b) => ({ ...b, anomalie: (data?.items || []).filter((i) => i.severita === "alta").length })); }).catch(() => {});
    };
    tick();
    const t = setInterval(tick, 30000);
    const onFocus = () => tick();
    window.addEventListener("focus", onFocus);
    return () => { stopped = true; clearInterval(t); window.removeEventListener("focus", onFocus); };
  }, [user?.role]);

  const roleBadge = { admin: "Amministratore", capocantiere: "Capocantiere", operaio: "Operaio" }[user?.role] || "";
  const isAdmin = user?.role === "admin";

  const renderLink = (l) => {
    const badge = l.badgeKey ? badges[l.badgeKey] : 0;
    return (
      <NavLink
        key={l.to}
        to={l.to}
        end={l.to === "/"}
        data-testid={l.testid}
        onClick={() => setOpen(false)}
        className={({ isActive }) => cn(
          "flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors",
          isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary hover:text-foreground"
        )}
      >
        <l.icon className="h-4 w-4" />
        <span className="flex-1">{l.label}</span>
        {badge > 0 && (
          <span data-testid={`badge-${l.testid}`} className="min-w-[20px] h-5 px-1.5 rounded-full bg-red-600 text-white text-[10px] font-extrabold flex items-center justify-center shrink-0">
            {badge > 99 ? "99+" : badge}
          </span>
        )}
      </NavLink>
    );
  };

  const renderGroup = (g) => {
    const expanded = !!groups[g.key];
    const groupBadge = (g.children || []).reduce((acc, c) => acc + (c.badgeKey ? badges[c.badgeKey] || 0 : 0), 0);
    return (
      <div key={g.key}>
        <button
          data-testid={g.testid}
          onClick={() => setGroups((s) => ({ ...s, [g.key]: !expanded }))}
          className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-semibold text-foreground hover:bg-secondary transition-colors"
        >
          <g.icon className="h-4 w-4 text-primary" />
          <span className="flex-1 text-left">{g.label}</span>
          {groupBadge > 0 && !expanded && (
            <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-red-600 text-white text-[10px] font-extrabold flex items-center justify-center">{groupBadge}</span>
          )}
          {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </button>
        {expanded && (
          <div className="ml-3 pl-3 border-l border-border mt-1 space-y-0.5">
            {g.children.map(renderLink)}
          </div>
        )}
      </div>
    );
  };

  const navContent = isAdmin
    ? ADMIN_NAV.map((item) => item.type === "group" ? renderGroup(item) : renderLink(item))
    : (SIMPLE_NAV[user?.role] || []).map(renderLink);

  return (
    <div className="min-h-screen bg-background flex">
      <aside className={cn(
        "fixed inset-y-0 left-0 z-40 w-72 bg-card border-r border-border transform transition-transform duration-200 lg:translate-x-0 lg:static lg:z-0 flex flex-col",
        open ? "translate-x-0" : "-translate-x-full"
      )}>
        <div className="h-20 px-6 flex items-center gap-3 border-b border-border shrink-0">
          <div className="h-10 w-10 rounded-lg bg-primary flex items-center justify-center">
            <HardHat className="h-5 w-5 text-primary-foreground" />
          </div>
          <div>
            <div className="font-bold text-lg tracking-tight">Cantiere360</div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Gestionale Edile</div>
          </div>
        </div>
        <nav className="p-3 space-y-1 flex-1 overflow-y-auto">
          {navContent}
        </nav>
        <div className="p-4 border-t border-border bg-card shrink-0">
          <InstallPWA className="w-full mb-3" variant="outline" size="sm" label="Installa Cantiere360" />
          <div className="flex items-center gap-3 px-2 pb-3">
            <div className="h-9 w-9 rounded-full bg-secondary flex items-center justify-center font-semibold text-sm">
              {(user?.name || user?.email || "?").slice(0, 2).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{user?.name || user?.email}</div>
              <div className="text-[10px] uppercase tracking-wider text-primary font-semibold">{roleBadge}</div>
            </div>
            <button data-testid="theme-toggle" onClick={toggleTheme} className="p-2 rounded-md hover:bg-secondary" title={theme === "dark" ? "Tema chiaro" : "Tema scuro"}>
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>
          </div>
          <Button data-testid="logout-btn" variant="outline" size="sm" className="w-full" onClick={async () => { await logout(); nav("/login"); }}>
            <LogOut className="h-4 w-4 mr-2" /> Esci
          </Button>
        </div>
      </aside>

      <div className="flex-1 min-w-0">
        <header className="lg:hidden sticky top-0 z-30 h-16 px-4 flex items-center justify-between bg-background/85 backdrop-blur-xl border-b border-border">
          <button data-testid="mobile-menu" onClick={() => setOpen((v) => !v)} className="p-2 rounded-md hover:bg-secondary">
            <Menu className="h-5 w-5" />
          </button>
          <div className="flex items-center gap-2"><HardHat className="h-4 w-4 text-primary" /><span className="font-bold">Cantiere360</span></div>
          <div className="w-9" />
        </header>
        <main className="p-4 md:p-8 max-w-7xl mx-auto pb-28">
          <ErrorBoundary>
            <Outlet />
          </ErrorBoundary>
        </main>
      </div>

      <VoiceAssistant />
      <DuplicateResolver />
      <MagazzinoReview />
      <PagamentoMetodoDialog />
    </div>
  );
}
